import React, { useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Mail, KeyRound } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/useAuthStore";
import { safeRedirectPath } from "@/lib/redirect";
import { supabase } from "@/services/supabase";
import {
  SUPABASE_PUBLISHABLE_KEY,
  SUPABASE_URL,
  TURNSTILE_SITE_KEY,
} from "@/config/publicConfig";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 登录后回跳（例如 /redeem）；只接受站内路径，防开放重定向
  const redirectPath = safeRedirectPath(searchParams.get("redirect"), "/app");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const { signInWithOAuth } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loginMethod, setLoginMethod] = useState<"password" | "magiclink">(
    "password",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      setErrorMsg(t("auth.complete_captcha_first"));
      return;
    }

    setLoading(true);
    setErrorMsg("");
    try {
      // Use the edge function instead of direct supabase client to pass turnstile token
      const res = await fetch(`${SUPABASE_URL}/functions/v1/auth-turnstile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          action: "signIn",
          email,
          password,
          turnstileToken,
        }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || t("auth.login_failed"));
      }

      // The edge function logs us in, but we still need to set the session locally
      // Supabase's setSession will save it to local storage
      if (data.session) {
        await supabase.auth.setSession(data.session);
      }

      navigate(redirectPath);
    } catch (err: any) {
      setErrorMsg(err.message || t("auth.login_failed"));
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      setErrorMsg(t("auth.complete_captcha_first"));
      return;
    }

    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/auth-turnstile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          action: "signInWithOtp",
          email,
          turnstileToken,
        }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || t("auth.send_failed"));
      }

      alert(t("auth.magic_link_sent_alert"));
      navigate("/");
    } catch (err: any) {
      setErrorMsg(err.message || t("auth.send_failed"));
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setErrorMsg(t("auth.enter_email_first"));
      return;
    }
    if (!turnstileToken) {
      setErrorMsg(t("auth.complete_captcha_first"));
      return;
    }

    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/auth-turnstile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          action: "resetPassword",
          email,
          turnstileToken,
        }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || t("auth.send_failed"));
      }

      alert(t("auth.reset_email_sent_alert"));
    } catch (err: any) {
      setErrorMsg(err.message || t("auth.send_failed"));
    } finally {
      setLoading(false);
      turnstileRef.current?.reset();
      setTurnstileToken("");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-pink-50 p-4">
      <img src="/favicon.png" alt="Logo" className="w-32 h-32 mb-4 rounded-3xl shadow-md" />
      <h1 className="text-3xl font-bold text-pink-600 mb-8">{t("app.title")}</h1>

      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">
          {t("auth.welcome_back")}
        </h2>

        {errorMsg && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm text-center">
            {errorMsg}
          </div>
        )}

        {/* Toggle Login Method */}
        <div className="flex mb-6 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => {
              setLoginMethod("password");
              setErrorMsg("");
            }}
            className={`flex-1 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${loginMethod === "password" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
          >
            <KeyRound size={16} />
            {t("auth.password_login")}
          </button>
          <button
            onClick={() => {
              setLoginMethod("magiclink");
              setErrorMsg("");
            }}
            className={`flex-1 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${loginMethod === "magiclink" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
          >
            <Mail size={16} />
            {t("auth.magic_link_login")}
          </button>
        </div>

        {/* Dynamic Form */}
        {loginMethod === "password" ? (
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.email_label")}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-pink-500 focus:border-pink-500"
                placeholder={t("auth.email_placeholder")}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.password")}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border rounded focus:ring-pink-500 focus:border-pink-500"
                placeholder={t("auth.password_placeholder")}
                required
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-pink-600 hover:text-pink-700"
              >
                {t("auth.forgot_password")}
              </button>
            </div>

            <div className="flex justify-center mt-4 min-h-[65px]">
              {TURNSTILE_SITE_KEY ? (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setErrorMsg(t("auth.captcha_failed"))}
                  onExpire={() => setTurnstileToken("")}
                />
              ) : (
                <div className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200 text-center">
                  人机验证服务尚未配置 (缺少 VITE_TURNSTILE_SITE_KEY)
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !turnstileToken}
              className="w-full py-2 px-4 bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50 transition-colors mt-6"
            >
              {loading ? t("auth.logging_in") : t("auth.login_btn")}
            </button>
          </form>
        ) : (
          <form onSubmit={handleMagicLink} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.email_label")}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-pink-500 focus:border-pink-500"
                placeholder={t("auth.email_placeholder")}
                required
              />
            </div>

            <div className="flex justify-center mt-4 min-h-[65px]">
              {TURNSTILE_SITE_KEY ? (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setErrorMsg(t("auth.captcha_failed"))}
                  onExpire={() => setTurnstileToken("")}
                />
              ) : (
                <div className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200 text-center">
                  人机验证服务尚未配置 (缺少 VITE_TURNSTILE_SITE_KEY)
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !turnstileToken}
              className="w-full py-2 px-4 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 transition-colors"
            >
              {loading ? t("auth.sending") : t("auth.send_magic_link")}
            </button>
          </form>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={async () => {
              try {
                setErrorMsg("");
                await signInWithOAuth("apple", redirectPath);
              } catch (e: any) {
                setErrorMsg(e?.message || "苹果登录失败，请重试");
              }
            }}
            className="w-full py-2 px-4 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
          >
            {t("auth.apple_login")}
          </button>
        </div>

        <div className="mt-6">
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t("auth.no_account")}</span>
            </div>
          </div>
          <Link
            to="/register"
            className="w-full flex items-center justify-center py-2 px-4 border-2 border-pink-500 text-pink-600 font-medium rounded-lg hover:bg-pink-50 transition-colors"
          >
            {t("auth.go_to_register_btn")}
          </Link>
        </div>
      </div>
    </div>
  );
}
